import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CreateLeadPayload,
  LeadDetailsPayload,
  LeadsListParams,
  leadsService,
  OnboardPayload,
  PaymentInstallmentRow,
  ProductDetailsPayload,
  UpdateLeadPayload,
} from '@/services/leads.service';
import { QUERY_KEYS } from '@/utils/constants';

export const useLeads = (params: LeadsListParams) =>
  useQuery({
    queryKey: [...QUERY_KEYS.LEADS.LIST, params],
    queryFn: () => leadsService.list(params),
    placeholderData: keepPreviousData,
  });

/**
 * One lead, described from the point of view of a specific ORDER (ADR-048).
 * The Orders page routes by order id, so the DTO must describe THAT purchase -
 * without the id, an older completed order would render as the client's latest.
 * `staleTime: 0` so reopening always reflects the newest saved cart.
 */
export const useLead = (leadId: number | null, orderId?: number | null) =>
  useQuery({
    queryKey: [...QUERY_KEYS.LEADS.BY_ID(leadId as number), orderId ?? 'current'],
    queryFn: () => leadsService.getById(leadId as number, orderId),
    enabled: leadId != null && Number.isFinite(leadId) && leadId > 0,
    staleTime: 0,
  });

// Per-type T&C acceptance status for a lead, shown as badges in the wizard.
// Polls + refetches on window focus so the badge flips automatically once the
// lead accepts on the public page (a different browser/session), without the RM
// having to refresh.
export const useLeadTcStatus = (id: number, enabled = true) =>
  useQuery({
    queryKey: QUERY_KEYS.LEADS.TC_STATUS(id),
    queryFn: () => leadsService.getTcStatus(id),
    enabled: enabled && Number.isFinite(id) && id > 0,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });

// Clickable T&C acceptance link(s) for a lead. Fetched on demand (the RM clicks
// "show links") since resolving them ensures the token rows exist.
export const useLeadTcLinks = (id: number, enabled: boolean) =>
  useQuery({
    queryKey: [...QUERY_KEYS.LEADS.TC_STATUS(id), 'links'],
    queryFn: () => leadsService.getTcLinks(id),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });

export const useInvalidateLeads = () => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.LEADS.LIST });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SALES.DASHBOARD_ROOT });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SALES.CLIENTS });
  };
};

// These mutations reconcile the cached lists via onSettled (success AND error),
// not just onSuccess: on a slow/flaky connection a request can be applied
// server-side while the client aborts (see apiClient timeout), and an
// onSuccess-only refresh would leave the tables showing stale rows (e.g. an ARN
// edit that saved but "failed" on the client). Refetching on error self-heals.
export const useCreateLead = () => {
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: (payload: CreateLeadPayload) => leadsService.create(payload),
    onSettled: invalidate,
  });
};

export const useImportLeads = () => {
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: (leads: CreateLeadPayload[]) => leadsService.import(leads),
    onSettled: invalidate,
  });
};

export const useUpdateLead = () => {
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateLeadPayload }) =>
      leadsService.update(id, payload),
    onSettled: invalidate,
  });
};

export const useOnboardLead = () => {
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: OnboardPayload }) =>
      leadsService.onboard(id, payload),
    onSettled: invalidate,
  });
};

// Live save of Step 1 (Lead Details) to the lead_details table - fire-and-forget,
// no list invalidation on every keystroke (ADR-017).
export const useSaveLeadDetails = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: LeadDetailsPayload }) =>
      leadsService.saveDetails(id, payload),
  });

// Live save of Step 2 (Product Details) to the product_details table -
// fire-and-forget like Step 1, no list invalidation on every change (ADR-018).
export const useSaveProductDetails = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ProductDetailsPayload }) =>
      leadsService.saveProductDetails(id, payload),
  });

export const useLookupArn = () =>
  useMutation({
    mutationFn: ({ arn, excludeLeadId }: { arn: string; excludeLeadId?: number }) =>
      leadsService.lookupArn(arn, excludeLeadId),
  });

export const useLookupGst = () =>
  useMutation({
    mutationFn: (gstin: string) => leadsService.lookupGst(gstin),
  });

export const useDeleteLead = () => {
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: (id: number) => leadsService.remove(id),
    onSettled: invalidate,
  });
};

// Step 3 (Payment Details): fetches the saved installment schedule for a lead.
// staleTime 0 so it always reflects the latest save when the step mounts.
// Keyed on the ORDER as well as the client: a schedule belongs to one purchase
// (ADR-048), so two orders of the same client must not share a cache entry.
export const usePaymentInstallments = (leadId: number, orderId?: number | null) =>
  useQuery({
    queryKey: ['payment-installments', leadId, orderId ?? null],
    queryFn: () => leadsService.getPaymentInstallments(leadId, orderId),
    enabled: Number.isFinite(leadId) && leadId > 0,
    staleTime: 0,
  });

// Live save of the payment schedule. Invalidates the shared installments query
// on settle so the Payment Plan and Billing Handover steps never read a stale
// copy of each other's edits within a session (they share this query key).
export const useSavePaymentInstallments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      installments,
      orderId,
    }: {
      id: number;
      installments: PaymentInstallmentRow[];
      orderId?: number | null;
    }) => leadsService.savePaymentInstallments(id, installments, orderId),
    onSettled: (_data, _err, variables) => {
      // Prefix match, so both the order-scoped key and any legacy lead-only one
      // are refreshed.
      void queryClient.invalidateQueries({ queryKey: ['payment-installments', variables.id] });
    },
  });
};
